/* @flow */
import * as React from 'react';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';
import RequireForAccess, { ReactAccessConsumer } from 'react-access';
import { Prompt } from 'react-router-dom';

import Paper from '@material-ui/core/Paper';

import ElectroResponseMessage from 'forms/components/inputs/ElectroResponseMessage';
import Activity from 'components/Activity/Activity';
import UserProfileRole from 'components/User/UserProfileRole';
import {
  autoCompleteCountries,
  autoCompleteCurrencies,
  autoCompletePriceFormats,
  autoCompleteTradingAccounts,
  autoCompleteRegions,
} from 'api/forms';
import { generateUrl, user } from 'api/routes';
import { updateRegion, viewRegion } from 'permissions/account';
import UserStore from 'stores/UserStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { customerUsers, internalUsers } from 'permissions/userTypes';
import { ENV } from 'config';
import AutoCompleteModel from 'models/forms/AutoCompleteModel';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import PageTitle from 'components/PageTitleLegacy/PageTitle';
import FormSection from 'components/FormSection/FormSection';
import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelect from 'forms/components/inputs/ElectroSelect/ElectroSelect';
import ElectroSelectExtra from 'forms/components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import FormControls from 'forms/components/controls/FormControls/FormControls';
import Loading from 'components/Loading/Loading';

import activityStyles from 'common-scss/DetailsPage.module.scss';
import './UserProfile.module.scss';

import { getForm } from './setupUserProfile';
import UserNotificationSelection from './components/UserNotificationSelection';
import AuthenticationStore from 'stores/AuthenticationStore';

type UserProfileProps = {
  form: Object,
};
type UserProfileInjectedProps = {
  userStore: UserStore,
} & TranslatorProps;

@translate()
@inject('userStore')
@observer
class UserProfile extends ComponentWithInjectedProps<
  UserProfileProps,
  {},
  UserProfileInjectedProps
> {
  @observable localState = {
    selectedValue: '',
    selectedAccounts: '',
  };

  @observable locationCountry: AutoCompleteModel = new AutoCompleteModel(autoCompleteCountries);

  @observable currenciesModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCurrencies);

  @observable priceFormatsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompletePriceFormats
  );

  @observable regionsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteRegions);

  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteTradingAccounts);

  handleOnChange = (model: Object, selectedValue: Object) => {
    model.setResult(selectedValue);
  };

  render() {
    const { form, userStore, t } = this.props;
    const userId = userStore.activeUser ? userStore.activeUser.id : null;
    const userRoleId = userStore.userRoleId;

    return (
      <div className={activityStyles['grid-wrapper']}>
        <Paper square className={activityStyles['form-item']}>
          {userStore.activeUser && (
            <UserProfileRole
              userDetails={userStore.activeUser}
              userTypes={userRoleId && userRoleId > 5 ? internalUsers : customerUsers}
              userRole={userRoleId}
            />
          )}

          <div className={activityStyles['form-item-wrapper']}>
            <a href={process.env.REACT_APP_MYER_PROFILE_PAGE}>
              <svg className="button-icon">
                <use xlinkHref={'#icon-edit'} />
              </svg>
              {t('general:manage_profile')}
            </a>

            <FormSection form={form} section="userProfile">
              <ElectroTextField field={form.$('userProfile.firstName')} disabled />
              <ElectroTextField field={form.$('userProfile.lastName')} disabled />
              <ElectroTextField
                field={form.$('userProfile.email')}
                disabled
                mask={{ type: 'email', shouldMask: ENV === 'demo' }}
              />
              <ElectroTextField field={form.$('userProfile.phoneNumber')} disabled />
              <ElectroTextField field={form.$('userProfile.stateCounty')} disabled />
              <ElectroTextField field={form.$('userProfile.city')} disabled />
              <ElectroTextField field={form.$('userProfile.postcode')} disabled />
              {this.locationCountry && (
                <ElectroSelect
                  disabled
                  field={form.$('userProfile.country')}
                  model={this.locationCountry}
                  onChange={(selectedValue: Object) => {
                    form.$('userProfile.country').value = selectedValue;
                    this.handleOnChange(this.locationCountry, selectedValue);
                  }}
                />
              )}
              <ElectroTextField field={form.$('userProfile.company')} disabled />
              <RequireForAccess permissions={[...updateRegion, ...viewRegion]}>
                <ReactAccessConsumer>
                  {authorizeAccess => {
                    if (this.regionsModel) {
                      return (
                        <ElectroSelectExtra
                          disabled={!authorizeAccess(updateRegion)}
                          field={form.$('userProfile.region')}
                          form={form}
                          formName="userProfile"
                          labelKey="name"
                          props={{
                            model: this.regionsModel,
                            modelChild: this.accountsModel,
                          }}
                        />
                      );
                    }
                  }}
                </ReactAccessConsumer>
              </RequireForAccess>
            </FormSection>
            <FormSection form={form} section="displaySettings">
              {this.currenciesModel && (
                <ElectroSelect
                  field={form.$('displaySettings.currency')}
                  model={this.currenciesModel}
                  onChange={selectedValue => {
                    form.$('displaySettings.currency').value = selectedValue;
                  }}
                  searchable={false}
                />
              )}
              {this.priceFormatsModel && (
                <ElectroSelect
                  field={form.$('displaySettings.price')}
                  model={this.priceFormatsModel}
                  onChange={selectedValue => {
                    form.$('displaySettings.price').value = selectedValue;
                  }}
                  searchable={false}
                  labelKey="format"
                />
              )}
            </FormSection>
            <FormSection form={form} section="notificationSettings">
              <UserNotificationSelection field={form.$('notificationSettings')} />
            </FormSection>
            <FormControls form={form} buttonText={t('general:actions.save')} />
            <Prompt
              when={form.isDirty}
              message="You have unsaved changes, are you sure you want to leave?"
            />
          </div>
        </Paper>

        <Paper square className={activityStyles['activity-panel']}>
          {userId && (
            <Activity
              title
              url={generateUrl(user.activity, [userId])}
              allowFilter
              refresh={userStore.activeUser}
            />
          )}
        </Paper>
      </div>
    );
  }
}

type UserProfileWrapperInjectedProps = {
  authenticationStore: AuthenticationStore,
  userStore: UserStore,
} & TranslatorProps;

@translate()
@inject('authenticationStore', 'userStore')
@observer
class UserProfileWrapper extends ComponentWithInjectedProps<
  {},
  {},
  UserProfileWrapperInjectedProps
> {
  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  componentDidMount() {
    const { userStore } = this.props;
    userStore.getUserLocations();
    userStore.getUserLanguages();
    userStore.getUserCurrencies();
    userStore.getUserPriceFormats();
    userStore.getActiveUser();
  }

  render() {
    const { userStore, t, i18n } = this.props;
    const formData = userStore.getActiveUserFromLocal();

    const form = getForm(formData, userStore, this.localState, t, i18n);

    return userStore.activeUser ? (
      <PageWrapper isFlex={false}>
        <ElectroResponseMessage localState={this.localState} />
        <PageTitle title={form.name} />
        <UserProfile form={form} />
      </PageWrapper>
    ) : (
      <Loading />
    );
  }
}

export default UserProfileWrapper;
